import styled, { keyframes } from 'styled-components';

const loadingBlink = keyframes`
  0%, 80%, 100% {
    opacity: 0.2;
    transform: translateY(0);
  }
  40% {
    opacity: 1;
    transform: translateY(-2px);
  }
`;

export const LoadingIndicator = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 0;

  & > span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.text.secondary};
    animation: ${loadingBlink} 1.2s infinite ease-in-out;
  }

  & > span:nth-child(2) {
    animation-delay: 0.2s;
  }

  & > span:nth-child(3) {
    animation-delay: 0.4s;
  }
`;

export const ChatContainer = styled.div`
  position: fixed;
  left: 16px;
  bottom: 16px;
  width: min(360px, 92vw);
  height: 460px;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.background.paper};
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 12px;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  z-index: 9999;
  backdrop-filter: blur(6px);
`;

export const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
`;

export const ChatTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
`;

export const HeaderActions = styled.div`
  display: flex;
  gap: 8px;

  & > button {
    height: 28px;
    width: 28px;
    display: grid;
    place-items: center;
    border-radius: 6px;
    border: 1px solid ${({ theme }) => theme.colors.divider};
    background: transparent;
    color: ${({ theme }) => theme.colors.text.primary};
    cursor: pointer;
  }
`;

export const ChatBody = styled.div`
  flex: 1;
  padding: 12px;
  display: flex;
  flex-direction: column;
  overflow: auto;
  gap: 4px;
`;

export const ChatFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px 12px 12px;
  border-top: 1px solid ${({ theme }) => theme.colors.divider};
`;

export const InputField = styled.input`
  flex: 1;
  height: 36px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  background: ${({ theme }) => theme.colors.background.paper} !important;
  color: ${({ theme }) => theme.colors.text.primary} !important;
  outline: none;
  padding: 0 10px;
  font-size: 14px;

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.secondary};
    opacity: 1;
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary.main};
    color: ${({ theme }) => theme.colors.text.primary} !important;
  }
`;

export const SendButton = styled.button`
  height: 36px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  background: ${({ theme }) => theme.colors.background.elevated};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.action.hover};
  }
`;

export const ToggleClosedButton = styled.button`
  position: fixed;
  left: 16px;
  bottom: 16px;
  width: 48px;
  height: 48px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  background: ${({ theme }) => theme.colors.background.paper};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  z-index: 9999;
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

export const SuggestedQuestionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const SuggestedQuestionChip = styled.button`
  background: ${({ theme }) =>
    theme.mode === 'light'
      ? `${theme.colors.primary.main}15`
      : `${theme.colors.primary.main}25`};
  border: 1px solid
    ${({ theme }) =>
      theme.mode === 'light'
        ? `${theme.colors.primary.main}80`
        : `${theme.colors.primary.main}60`};
  border-radius: 12px;
  padding: 8px 12px;
  font-size: 12px;
  color: ${({ theme }) =>
    theme.mode === 'light'
      ? theme.colors.primary.dark
      : theme.colors.primary.main};
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;
  font-weight: 500;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'light'
        ? `${theme.colors.primary.main}25`
        : `${theme.colors.primary.main}35`};
    border-color: ${({ theme }) => theme.colors.primary.main};
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const WelcomeMessage = styled.div`
  opacity: 0.8;
  margin-bottom: 16px;
`;

export const SuggestedQuestionsLabel = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 12px;
`;

export const FollowUpQuestionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.colors.divider};
`;

export const FollowUpQuestionChip = styled.button`
  background: ${({ theme }) =>
    theme.mode === 'light'
      ? `${theme.colors.success.main}15`
      : `${theme.colors.success.main}25`};
  border: 1px solid
    ${({ theme }) =>
      theme.mode === 'light'
        ? `${theme.colors.success.main}80`
        : `${theme.colors.success.main}60`};
  border-radius: 10px;
  padding: 6px 10px;
  font-size: 11px;
  color: ${({ theme }) =>
    theme.mode === 'light'
      ? theme.colors.success.dark
      : theme.colors.success.main};
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;
  font-weight: 500;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'light'
        ? `${theme.colors.success.main}25`
        : `${theme.colors.success.main}35`};
    border-color: ${({ theme }) => theme.colors.success.main};
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const FollowUpQuestionsLabel = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 8px;
  font-style: italic;
`;

export const SeeOtherOptionsButton = styled.button`
  background: ${({ theme }) =>
    theme.mode === 'light'
      ? theme.colors.background.elevated
      : theme.colors.action.hover};
  border: 1px solid
    ${({ theme }) =>
      theme.mode === 'light'
        ? theme.colors.neutral[300]
        : theme.colors.divider};
  border-radius: 10px;
  padding: 6px 10px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  text-align: center;
  transition: all 0.2s ease;
  margin-top: 4px;
  font-style: italic;
  font-weight: 500;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'light'
        ? theme.colors.neutral[200]
        : theme.colors.action.selected};
    border-color: ${({ theme }) => theme.colors.text.secondary};
    color: ${({ theme }) => theme.colors.text.primary};
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const MessageBubble = styled.div<{ $isUser: boolean }>`
  margin-bottom: 8px;
  align-self: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
  max-width: 85%;
  background: ${({ theme, $isUser }) =>
    $isUser ? theme.colors.action.selected : theme.colors.background.elevated};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  padding: 8px 10px;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.text.primary};
`;
